/**
 * Global suggestion vs action mode for Koko in the studio.
 *
 * - **action**: bird executes tool calls — types, clicks submit, toggles
 *   chips, deletes, etc.
 * - **suggestion**: bird drafts in forms (submit=false) and navigates,
 *   but does not commit destructive or inline state changes.
 */

export type KokoInteractionMode = "suggestion" | "action";

const STORAGE_KEY = "koko-studio-interaction-mode";

/** Tools that commit on blur, click, or have no draft-only path. */
export const TOOLS_BLOCKED_IN_SUGGESTION_MODE = new Set([
  "delete_character",
  "delete_asset",
  "delete_location",
  "delete_scene",
  "delete_moodboard_item",
  "add_scene",
  "update_scene",
  "set_costume",
  "cast_scene",
  "set_storyboard_status",
  "update_sound_design",
  "set_scene_camera",
  "set_project_title",
  "render_final_cut",
]);

/** Add/update tools that support `submit: false` for draft-only typing. */
export const TOOLS_WITH_SUBMIT_PARAM = new Set([
  "add_character",
  "update_character",
  "add_asset",
  "update_asset",
  "add_location",
  "update_location",
  "add_moodboard_item",
  "add_storyboard_shot",
  "edit_surface_image",
]);

type ModeListener = (mode: KokoInteractionMode) => void;

let currentMode: KokoInteractionMode = readStoredMode();
const listeners = new Set<ModeListener>();

function readStoredMode(): KokoInteractionMode {
  if (typeof window === "undefined") return "action";
  const saved = window.localStorage.getItem(STORAGE_KEY);
  return saved === "suggestion" ? "suggestion" : "action";
}

export function getKokoInteractionMode(): KokoInteractionMode {
  return currentMode;
}

export function setKokoInteractionMode(mode: KokoInteractionMode): void {
  if (mode === currentMode) return;
  currentMode = mode;
  if (typeof window !== "undefined") {
    window.localStorage.setItem(STORAGE_KEY, mode);
    document.documentElement.dataset.kokoInteractionMode = mode;
  }
  for (const listener of listeners) listener(mode);
}

export function subscribeKokoInteractionMode(
  listener: ModeListener,
): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Resolve whether a tool call should click submit / commit.
 * Suggestion mode always drafts; explicit `submit: false` also drafts.
 */
export function resolveToolSubmit(explicitSubmit: boolean | null): boolean {
  if (getKokoInteractionMode() === "suggestion") return false;
  return explicitSubmit ?? true;
}

export function isToolBlockedInSuggestionMode(toolName: string): boolean {
  return TOOLS_BLOCKED_IN_SUGGESTION_MODE.has(toolName);
}

/** Apply persisted mode to the document root (call once on boot). */
export function applyKokoInteractionModeToDocument(): void {
  if (typeof document !== "undefined") {
    document.documentElement.dataset.kokoInteractionMode = currentMode;
  }
}
